import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import gettext from '@eb/gettext';
import uniqueId from 'lodash/uniqueId';
import { getFileFromDropEvent, getFileFromBrowseEvent } from './utils';
import {
    addWindowDragEvent,
    removeWindowDragEvent,
} from './windowDragEventUtils';
import { DRAG_END_EVENT, DRAG_START_EVENT } from './constants';

import { Label } from '@eb/eds-label';
import { TYPE_LABEL } from '@eb/eds-label';

import './uploaderDropzone.scss';

/**
 * A stateless utility component that handles drag and drop events
 * @extends React.PureComponent
 * @example
 * // <UploaderDropzone
 * //     onDragEvent={stateChangeFunc}
 * //     onFileDrop={fileDropFunc}
 * // >
 * //     Drag files here!
 * // </UploaderDropzone>
 */
export default class UploaderDropzone extends React.PureComponent {
    static propTypes = {
        /**
         * Function that's invoked when a drag event occurs
         */
        onDragEvent: PropTypes.func.isRequired,

        /**
         * Function that's invoked when a file is dropped
         */
        onFileDrop: PropTypes.func.isRequired,
        /**
         * The dropzone content
         */
        children: PropTypes.node,
        /**
         * React ref to externally trigger file upload click event
         */
        fileInputRef: PropTypes.oneOfType([
            PropTypes.func,
            PropTypes.shape({ current: PropTypes.any }),
        ]),
        /**
         * indicates if it is a dropzone component
         */
        isDropzone: PropTypes.bool,
    };

    constructor(props) {
        super(props);

        this.state = {
            isFocus: false,
        };
    }

    componentWillMount() {
        this._boundWindowDragEvent = this._handleDragEvent.bind(this);
        addWindowDragEvent(this._boundWindowDragEvent);
    }

    componentWillUnmount() {
        removeWindowDragEvent(this._boundWindowDragEvent);
    }

    _handleDragEvent(eventType, event) {
        const { onDragEvent } = this.props;

        event.preventDefault();

        if (eventType) {
            onDragEvent(eventType);
        }
    }

    _handleDrop(event) {
        const { onFileDrop, onDragEvent } = this.props;
        const file = getFileFromDropEvent(event);

        event.preventDefault();

        if (file) {
            onFileDrop(file);
        }
        onDragEvent(DRAG_END_EVENT);
    }

    _handleFileChanged(event) {
        const { onFileDrop, onDragEvent } = this.props;
        const file = getFileFromBrowseEvent(event);

        if (file) {
            onFileDrop(file);
        }
        onDragEvent(DRAG_END_EVENT);
    }

    _getPageLabelId() {
        if (!this._labelInputId) {
            this._labelInputId = uniqueId('uploader-dropzone-');
        }
        return this._labelInputId;
    }

    _handleOnFocus() {
        this.setState({
            isFocus: true,
        });
    }

    _handleOnBlur() {
        this.setState({
            isFocus: false,
        });
    }

    render() {
        const { children, fileInputRef, isDropzone } = this.props;
        const labelId = this._getPageLabelId();
        const dropzoneClassName = classNames(
            {
                'eds-uploader-dropzone--is-focus': this.state.isFocus,
            },
            'eds-uploader-dropzone',
        );
        const dropzoneLabelWrapperClassName = classNames({
            'eds-hide': fileInputRef && isDropzone,
        });

        return (
            <div
                className={dropzoneClassName}
                onDragOver={this._handleDragEvent.bind(this, null)}
                onDragEnter={this._handleDragEvent.bind(this, DRAG_START_EVENT)}
                onDragLeave={this._handleDragEvent.bind(this, DRAG_END_EVENT)}
                onDrop={this._handleDrop.bind(this)}
                data-spec="uploader-dropzone"
            >
                <div
                    className="eds-uploader-dropzone__content"
                    data-spec="dropzone-content"
                >
                    {children}
                </div>
                <Label
                    type={TYPE_LABEL}
                    htmlFor={labelId}
                    className={dropzoneLabelWrapperClassName}
                >
                    <span
                        className="eds-uploader-dropzone__cover"
                        data-spec="dropzone-cover"
                    >
                        <span className="eds-is-hidden-accessible">
                            {gettext('Choose a file')}
                            <input
                                type="file"
                                className="eds-uploader-dropzone__browse-input"
                                data-spec="dropzone-browse-input"
                                onChange={this._handleFileChanged.bind(this)}
                                onFocus={this._handleOnFocus.bind(this)}
                                onBlur={this._handleOnBlur.bind(this)}
                                id={labelId}
                                ref={fileInputRef}
                            />
                        </span>
                    </span>
                </Label>
            </div>
        );
    }
}
